/**
 * @theme/pickup-availability — fetches per-variant pickup info via Section Rendering API.
 * Plus `<pickup-availability-drawer>` companion modal.
 */

import { Component, defineComponent } from '@theme/component';
import { trapFocus, releaseFocusTrap } from '@theme/focus';

/**
 * @extends {Component}
 */
export class PickupAvailability extends Component {
  /** @type {Element | null} */ errorHtml = null;

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('available')) return;
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template'));
    if (template?.content.firstElementChild) this.errorHtml = template.content.firstElementChild.cloneNode(true);
    this.fetchAvailability(this.dataset.variantId ?? '');
  }

  /** @param {string} variantId */
  async fetchAvailability(variantId) {
    let rootUrl = this.dataset.rootUrl ?? '/';
    if (!rootUrl.endsWith('/')) rootUrl = `${rootUrl}/`;
    const url = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;
    try {
      const res = await fetch(url);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
      if (html instanceof HTMLElement) this.#renderPreview(html);
    } catch {
      this.querySelector('button')?.removeEventListener('click', this.#onRefresh);
      this.#renderError();
    }
  }

  #onRefresh = () => this.fetchAvailability(this.dataset.variantId ?? '');

  #renderError() {
    this.innerHTML = '';
    if (this.errorHtml) this.appendChild(this.errorHtml);
    this.querySelector('button')?.addEventListener('click', this.#onRefresh);
  }

  /** @param {HTMLElement} sectionInner */
  #renderPreview(sectionInner) {
    document.querySelector('pickup-availability-drawer')?.remove();
    const preview = sectionInner.querySelector('pickup-availability-preview');
    if (!preview) {
      this.innerHTML = '';
      this.removeAttribute('available');
      return;
    }
    this.innerHTML = preview.outerHTML;
    this.setAttribute('available', '');

    const drawer = sectionInner.querySelector('pickup-availability-drawer');
    if (drawer) document.body.appendChild(drawer);

    const button = this.querySelector('button');
    button?.addEventListener('click', (event) => {
      /** @type {any} */ (document.querySelector('pickup-availability-drawer'))?.show?.(event.target);
    });
  }
}

defineComponent('pickup-availability', PickupAvailability);

/**
 * @extends {Component}
 */
export class PickupAvailabilityDrawer extends Component {
  /** @type {HTMLElement | null} */ focusElement = null;

  connectedCallback() {
    super.connectedCallback();
    this.querySelector('button')?.addEventListener('click', this.hide);
    this.addEventListener('keyup', this.#onKeyup);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.removeEventListener('click', this.#handleBodyClick);
  }

  /** @param {KeyboardEvent} event */
  #onKeyup = (event) => { if (event.code?.toUpperCase() === 'ESCAPE') this.hide(); };

  /** @param {MouseEvent} event */
  #handleBodyClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target !== this && !target.closest('pickup-availability-drawer') && target.id !== 'ShowPickupAvailabilityDrawer') {
      this.hide();
    }
  };

  hide = () => {
    this.removeAttribute('open');
    document.body.removeEventListener('click', this.#handleBodyClick);
    document.body.classList.remove('overflow-hidden');
    if (this.focusElement) releaseFocusTrap(this.focusElement);
  };

  /** @param {HTMLElement} focusElement */
  show(focusElement) {
    this.focusElement = focusElement;
    this.setAttribute('open', '');
    document.body.addEventListener('click', this.#handleBodyClick);
    document.body.classList.add('overflow-hidden');
    trapFocus(this);
  }
}

defineComponent('pickup-availability-drawer', PickupAvailabilityDrawer);
